/*
 * Назначение: страница «Интеграции».
 * Функционал:
 * - блок «Прокси для Telegram»: настройка прокси для исходящих запросов
 *   к api.telegram.org (РФ-хостинги его блокируют). После сохранения —
 *   тест связи через прокси.
 * - блок «Провайдеры нейросети»: ключи доступа к API в стиле OpenAI
 *   с показом/скрытием ключа и возможностью добавить несколько провайдеров
 *   (fallback между ними при сбое).
 *
 * Токены ботов (Telegram, MAX) и включение каналов настраиваются НЕ здесь,
 * а в настройках конкретного проекта (Настройки → Каналы), потому что у
 * каждого проекта свой бот.
 *
 * Видеть страницу могут только OWNER и ADMIN.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Eye, EyeOff, Loader2, Plug, Plus, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { integrationsApi, type AiProvider, type SmtpConfig, type TelegramProxyConfig } from "../../lib/admin-api";
import { ApiError } from "../../lib/api";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";

const EMPTY: TelegramProxyConfig = {
  enabled: false,
  type: "http",
  host: "",
  port_http: 0,
  port_socks5: 0,
  username: "",
  password: "",
};

export function IntegrationsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["integrations", "telegram-proxy"],
    queryFn: () => integrationsApi.getTelegramProxy(),
  });

  const [form, setForm] = useState<TelegramProxyConfig>(EMPTY);

  // Подтягиваем серверные значения в локальную форму, когда они пришли.
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: TelegramProxyConfig) => integrationsApi.updateTelegramProxy(payload),
    onSuccess: (saved) => {
      toast.success("Настройки прокси сохранены");
      setForm(saved);
      queryClient.invalidateQueries({ queryKey: ["integrations", "telegram-proxy"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить настройки");
    },
  });

  const testMutation = useMutation({
    mutationFn: () => integrationsApi.testTelegram(),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(`Связь есть. Бот: @${res.bot_username ?? "—"}`);
      } else {
        toast.error(res.detail || "Telegram недоступен");
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Ошибка теста");
    },
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Загружаем настройки…
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <p className="text-destructive">Не удалось загрузить настройки интеграций.</p>
      </div>
    );
  }

  const proxyChanged =
    !!data &&
    JSON.stringify({
      enabled: data.enabled,
      type: data.type,
      host: data.host,
      port_http: data.port_http,
      port_socks5: data.port_socks5,
      username: data.username,
      password: data.password,
    }) !== JSON.stringify(form);

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Plug className="h-6 w-6" />
          Интеграции
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Глобальные настройки на всю инсталляцию.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Прокси для Telegram</CardTitle>
          <CardDescription>
            Российские хостинги блокируют исходящие запросы к api.telegram.org.
            Чтобы бот работал, укажи HTTP-прокси с зарубежной точкой выхода.
            После сохранения нажми «Проверить связь», чтобы убедиться, что
            прокси и токен корректны.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="proxy-enabled">Использовать прокси</Label>
              <p className="text-xs text-muted-foreground">
                Если выключить — система будет ходить в Telegram напрямую.
              </p>
            </div>
            <Switch
              id="proxy-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => setForm((s) => ({ ...s, enabled: v }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="proxy-host">Адрес прокси</Label>
              <Input
                id="proxy-host"
                placeholder="например, 102.129.221.246"
                value={form.host}
                onChange={(e) => setForm((s) => ({ ...s, host: e.target.value.trim() }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="proxy-port-http">Порт HTTP(S)</Label>
              <Input
                id="proxy-port-http"
                type="number"
                inputMode="numeric"
                placeholder="например, 1947"
                value={form.port_http || ""}
                onChange={(e) =>
                  setForm((s) => ({ ...s, port_http: Number(e.target.value) || 0 }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="proxy-user">Логин</Label>
              <Input
                id="proxy-user"
                placeholder="user329779"
                value={form.username}
                onChange={(e) => setForm((s) => ({ ...s, username: e.target.value.trim() }))}
                autoComplete="off"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="proxy-pass">Пароль</Label>
              <Input
                id="proxy-pass"
                type="text"
                placeholder="cxs08o"
                value={form.password}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending || !proxyChanged}
            >
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
            <Button
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              title="Делает запрос getMe через прокси, проверяет токен из .env"
            >
              {testMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Проверить связь
            </Button>
          </div>

          {testMutation.data && (
            <div className="rounded-md border p-3 text-sm flex items-start gap-2">
              {testMutation.data.ok ? (
                <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              )}
              <div>
                {testMutation.data.ok ? (
                  <>
                    Связь с Telegram есть. Бот: <b>@{testMutation.data.bot_username}</b>
                    {testMutation.data.bot_id ? ` (id ${testMutation.data.bot_id})` : ""}.
                  </>
                ) : (
                  <span className="text-destructive">{testMutation.data.detail}</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AiProvidersCard />
      <SmtpCard />
    </div>
  );
}

function AiProvidersCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["integrations", "ai-providers"],
    queryFn: () => integrationsApi.getAiProviders(),
  });

  const [items, setItems] = useState<AiProvider[]>([]);
  const [shown, setShown] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (data) setItems(data.providers);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (providers: AiProvider[]) => integrationsApi.updateAiProviders(providers),
    onSuccess: (res) => {
      toast.success("Провайдеры сохранены");
      setItems(res.providers);
      queryClient.invalidateQueries({ queryKey: ["integrations", "ai-providers"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить");
    },
  });

  const addProvider = () => {
    setItems((s) => [
      ...s,
      {
        id: null,
        name: "Новый провайдер",
        api_key: "",
        // Адрес не подставляем: провайдер может быть любой, его задаёт оператор.
        base_url: "",
        enabled: true,
        order: s.length,
        timeout_seconds: 30,
      },
    ]);
  };

  const updateAt = (i: number, patch: Partial<AiProvider>) => {
    setItems((s) => s.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };

  const removeAt = (i: number) => {
    setItems((s) => s.filter((_, idx) => idx !== i));
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Провайдеры нейросети</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Загружаем…
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Провайдеры нейросети</CardTitle>
        <CardDescription>
          Ключи доступа к нейросети (любой провайдер с API в стиле OpenAI). Можно
          добавить несколько — если первый недоступен, система переключится на
          следующий по порядку. Первый включённый считается основным.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isError && (
          <p className="text-sm text-destructive">Не удалось загрузить провайдеров.</p>
        )}

        {items.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Провайдеров пока нет. Добавь первый: название, адрес API и ключ.
          </p>
        )}

        {items.map((p, i) => (
          <div key={i} className="rounded-md border p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={p.enabled}
                  onCheckedChange={(v) => updateAt(i, { enabled: v })}
                  id={`prov-en-${i}`}
                />
                <Label htmlFor={`prov-en-${i}`} className="text-xs text-muted-foreground">
                  {p.enabled ? "Включён" : "Выключен"}
                </Label>
                {i === 0 && p.enabled && (
                  <span className="text-xs text-success">основной</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => removeAt(i)}
                title="Удалить провайдера"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Название</Label>
                <Input
                  value={p.name}
                  onChange={(e) => updateAt(i, { name: e.target.value })}
                  placeholder="название провайдера"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Endpoint (URL API)</Label>
                <Input
                  value={p.base_url}
                  onChange={(e) => updateAt(i, { base_url: e.target.value.trim() })}
                  placeholder="адрес API провайдера (…/chat/completions)"
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Таймаут запроса, сек</Label>
                <Input
                  type="number"
                  min={5}
                  max={300}
                  value={p.timeout_seconds}
                  onChange={(e) =>
                    updateAt(i, { timeout_seconds: Number(e.target.value) })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Сколько ждать ответ. Медленным моделям нужно больше времени;
                  по истечении срока запрос уходит к следующей модели.
                </p>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Ключ API</Label>
                <div className="flex gap-2">
                  <Input
                    type={shown[i] ? "text" : "password"}
                    value={p.api_key}
                    onChange={(e) => updateAt(i, { api_key: e.target.value })}
                    placeholder={
                      p.has_key
                        ? `Ключ сохранён (${p.key_preview || "•••"}). Оставьте пустым, чтобы не менять`
                        : "ключ API провайдера"
                    }
                    className="font-mono text-xs"
                    autoComplete="off"
                    name={`ai-provider-key-${i}-nofill`}
                    data-1p-ignore
                    data-lpignore="true"
                    spellCheck={false}
                    readOnly
                    onFocus={(e) => e.currentTarget.removeAttribute("readonly")}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShown((s) => ({ ...s, [i]: !s[i] }))}
                    title={shown[i] ? "Скрыть" : "Показать"}
                  >
                    {shown[i] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {p.has_key && (
                  <p className="text-xs text-muted-foreground">
                    Сейчас сохранён ключ {p.key_preview}. Поле оставлено пустым
                    специально — впишите новый ключ, только если хотите заменить.
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}

        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button variant="outline" onClick={addProvider}>
            <Plus className="h-4 w-4 mr-1" />
            Добавить провайдера
          </Button>
          <Button
            onClick={() => saveMutation.mutate(items)}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


const EMPTY_SMTP: SmtpConfig = {
  enabled: false,
  host: "",
  port: 465,
  username: "",
  password: "",
  from_addr: "",
  from_name: "ИИ-продавец",
  use_ssl: true,
};

function SmtpCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["integrations", "smtp"],
    queryFn: () => integrationsApi.getSmtp(),
  });

  const [form, setForm] = useState<SmtpConfig>(EMPTY_SMTP);
  const [showPass, setShowPass] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: SmtpConfig) => integrationsApi.updateSmtp(payload),
    onSuccess: (saved) => {
      toast.success("Настройки почты сохранены");
      setForm(saved);
      queryClient.invalidateQueries({ queryKey: ["integrations", "smtp"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить");
    },
  });

  const testMutation = useMutation({
    mutationFn: () => integrationsApi.testSmtp(testTo.trim()),
    onSuccess: (res) => {
      if (res.ok) toast.success("Тестовое письмо отправлено");
      else toast.error(res.detail || "Не удалось отправить");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Ошибка отправки");
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Почта (SMTP)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Загружаем…
          </div>
        </CardContent>
      </Card>
    );
  }

  // Порт и SSL связаны: 465 → implicit SSL, 587/2525 → STARTTLS.
  const onPortChange = (port: number) => {
    setForm((s) => ({ ...s, port, use_ssl: port === 465 }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Почта (SMTP)</CardTitle>
        <CardDescription>
          Отправка уведомлений менеджеру на email. Нужен SMTP-сервер, логин и
          пароль (для Яндекс/Mail.ru — пароль приложения, не основной).
          Входящая почта (IMAP) не нужна — система только отправляет письма.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="smtp-enabled">Отправлять уведомления на email</Label>
            <p className="text-xs text-muted-foreground">
              Если выключено — уведомления идут только в Telegram.
            </p>
          </div>
          <Switch
            id="smtp-enabled"
            checked={form.enabled}
            onCheckedChange={(v) => setForm((s) => ({ ...s, enabled: v }))}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="smtp-host">SMTP-сервер</Label>
            <Input
              id="smtp-host"
              placeholder="например, smtp.yandex.ru"
              value={form.host}
              onChange={(e) => setForm((s) => ({ ...s, host: e.target.value.trim() }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="smtp-port">Порт</Label>
            <Select value={String(form.port)} onValueChange={(v) => onPortChange(Number(v))}>
              <SelectTrigger id="smtp-port">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="465">465 (SSL)</SelectItem>
                <SelectItem value="587">587 (STARTTLS)</SelectItem>
                <SelectItem value="2525">2525 (STARTTLS, запасной)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="smtp-user">Логин (обычно полный email)</Label>
            <Input
              id="smtp-user"
              placeholder="manager@example.ru"
              value={form.username}
              onChange={(e) => setForm((s) => ({ ...s, username: e.target.value.trim() }))}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="smtp-pass">Пароль (или пароль приложения)</Label>
            <div className="flex gap-2">
              <Input
                id="smtp-pass"
                type={showPass ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                autoComplete="off"
                spellCheck={false}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowPass((v) => !v)}
                title={showPass ? "Скрыть" : "Показать"}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="smtp-from">От кого (адрес)</Label>
            <Input
              id="smtp-from"
              placeholder="если пусто — берётся логин"
              value={form.from_addr}
              onChange={(e) => setForm((s) => ({ ...s, from_addr: e.target.value.trim() }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="smtp-from-name">Имя отправителя</Label>
            <Input
              id="smtp-from-name"
              value={form.from_name}
              onChange={(e) => setForm((s) => ({ ...s, from_name: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 pt-2 border-t">
          <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить
          </Button>
          <div className="flex items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="smtp-test-to" className="text-xs text-muted-foreground">
                Проверка: куда отправить
              </Label>
              <Input
                id="smtp-test-to"
                placeholder="email для теста"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="w-56"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || !testTo.trim()}
            >
              {testMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Отправить тест
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Если письма не уходят с таймаутом на портах 465/587 — хостинг
          блокирует их. На этот случай используй порт <b>2525</b> (запасной
          порт submission, его хостинги обычно не блокируют).
        </p>
      </CardContent>
    </Card>
  );
}
